import { delay, eventChannel, buffers, END } from 'redux-saga'
import {
	all,
	call,
	race,
	spawn,
	put,
	select,
	cancelled,
	takeLatest,
	take,
	actionChannel,
} from 'redux-saga/effects'

import config from 'config'
import mixpanel from 'helpers/mixpanel'
import exponentialBackoff from 'redux/sagas/helpers/exponentialBackoff'
import documentVisibility from 'redux/sagas/helpers/documentVisibility'
import actionTypes from 'redux/actions/actionTypes'
import whiteboardActions from 'redux/actions/whiteboardActions'
import formatError from './helpers/error_formatter'
import errorCodes from 'helpers/enums/errorCodes'
import socketConnect from 'sync/helpers/socketConnection'
import {callAnyWithCode as callAny} from './helpers/call_any'
import {
	startSession,
	getSession,
	getDrawing,
	startDrawing,
	findSessionByPhoenixSessionId,
	findDrawingBySession,
} from 'sync/whiteboard_sync'
import whiteboardRoles from 'helpers/enums/whiteboardRoles'
import whiteboardModes from 'helpers/enums/whiteboardModes'
import slideSelector from 'redux/selectors/whiteboard/slide_selector'
import actionsSelector from 'redux/selectors/whiteboard/actions_selector'
import drawingSelector from 'redux/selectors/whiteboard/drawing_selector'
import drawingIdSelector from 'redux/selectors/whiteboard/drawing_id_selector'

function *connectSocket(sessionId, jwt) {
	const socket = socketConnect(config.whiteboard_media_socket, ['websocket', 'polling'])
	let isConnected
	try {
		isConnected = yield call(socket.connect, {
			sessionId,
			jwt,
		})
	} catch (err) {
		isConnected = false
		socket.destroy()
	}
	if (isConnected) {
		return socket
	} else {
		throw new Error()
	}
}

function *startSocketConnection(sessionId, jwt) {
	let socket
	try {
		socket = yield call(exponentialBackoff, connectSocket, [sessionId, jwt])
		return socket
	} catch (err) {
		socket.disconnect()
	} finally {
		if (yield cancelled()) {
			socket.disconnect()
		}
	}
}

function *getByPhoenixId(phoenixSessionId, returnOnNotFound) {
	try {
		const response = yield callAny(findSessionByPhoenixSessionId, phoenixSessionId)
		return response.id
	} catch (err) {
		if (err && err.error_code === errorCodes.NOT_FOUND && returnOnNotFound) {
			return null
		} else {
			throw err
		}
	}
}

function *getSessionId(phoenixSessionId, role) {
	let response
	response = yield call(exponentialBackoff, getByPhoenixId, [phoenixSessionId, true])
	if (!response) {
		if (role === whiteboardRoles.TEACHER) {
			response = yield callAny(startSession, phoenixSessionId)
			return response
		} else {
			response = yield call(exponentialBackoff, getByPhoenixId, [phoenixSessionId, false])
		}
	}
	return response
}

function *getSessionData(id) {
	try {
		const whiteboard = yield callAny(getSession, id)

		yield put(whiteboardActions.whiteboardFetched(whiteboard))
		return whiteboard
	} catch (err) {

	}
}

function *getDrawingData() {
	try {
		const id = yield select(drawingIdSelector)
		const drawing = yield callAny(getDrawing, id)

		// debugging purposes only <--- start
		// if (role === 'student') {
		// 	yield call(delay, 5000)
		// }
		// debugging purposes only <--- end

		yield put(whiteboardActions.drawingFetched(drawing))
		return drawing
	} catch (err) {

	}
}

function *syncActions(socket, drawingData) {
	const actionsData = yield select(actionsSelector)
	const drawingId = yield select(drawingIdSelector)
	let syncedActions = drawingData.actions

	function *submitActions(currentActions) {
		let newActions = []

		// Drawing has been cleared or undos reached an empty
		if (currentActions.length === 0) {
			newActions.push(
				{
					index: -1,
					messageStatus: 'end signal',
				})
		// if currentActions length is lower an undo was performed
		} else if (currentActions.length < syncedActions.length) {
			// we should only push the last synced action if it is the same than current's action at that index
			// if it isn't it means undos have been replaced by newer actions and that will be taken care
			// later in the code
			// this case only signals to other clients that they have to discard newer indexes
			if (currentActions.length
				&& currentActions[currentActions.length - 1].actionId === syncedActions[currentActions.length - 1].actionId) {
				newActions.push({
					index: currentActions.length - 1,
					actionId: currentActions[currentActions.length - 1].actionId,
					messageStatus: 'end signal',
				})
			}
		} else if (currentActions.length !== syncedActions.length) {
			// New actions are added to the list
			newActions = currentActions
				.slice(syncedActions.length)
				.map((action, index) => ({
					...action,
					index: syncedActions.length + index,
				}))
		}
		// Comparing synced action indexes with new set of actions
		let actionIndex = Math.min(syncedActions.length, currentActions.length) - 1
		while (actionIndex >= 0) {
			if (syncedActions[actionIndex].actionId !== currentActions[actionIndex].actionId
				|| syncedActions[actionIndex].revision !== currentActions[actionIndex].revision
			) {
				newActions = [
					{
						...currentActions[actionIndex],
						index: actionIndex,
					},
					...newActions,
				]
			} else {
				break
			}
			actionIndex -= 1
		}

		// if possible sends partial data for last submitting action
		newActions = newActions.map(action => {
			const previousActionState = syncedActions[action.index]
			const currentActionState = currentActions[action.index]
			if (previousActionState &&
				previousActionState.actionId === currentActionState.actionId) {
				// For now only partial updated actions will be for traces
				if (action.traces) {
					action.traces = action.traces.slice(previousActionState.traces.length)
				}
			}
			return action
		})

		syncedActions = currentActions

		if (newActions.length) {
			yield spawn(socket.emit, 'submitActions', {
				actions: newActions,
				id: actionsData.id,
			})
			return true
		} else {
			return false
		}
	}

	function *listenToNewActions() {
		const eventActions = [
			actionTypes.WHITEBOARD_ACTIONS_CHANGED,
		]
		const requestChannel = yield actionChannel(eventActions, buffers.sliding(1))
		while (true) {
			const timeInterval = 100
			const action = yield take(requestChannel)
			try {
				const initTime = Date.now()
				const currentDrawingId = yield select(drawingIdSelector)
				if (currentDrawingId === drawingId) {
					const isSubmittingNewActions = yield call(submitActions, action.actions)
					yield all([
						call(delay, timeInterval),
						isSubmittingNewActions && take([actionTypes.WHITEBOARD_ACTIONS_RECEIVED]),
					])
					const totalTime = Date.now() - initTime
					const coolOffTime = Math.max(0, totalTime - timeInterval)
					if (coolOffTime > 500) {
						yield call(delay, Math.min(coolOffTime, 1000))
					}
				}

			} catch (err) {
				// console.log(err)
			}
		}
	}

	yield spawn(submitActions, actionsData.actions)
	yield call(listenToNewActions, actionsData.actions)

}

function subscribe(socket) {
	return eventChannel(emit => {
		socket.on('actions', message => {
			emit({type: 'actions', message})
		})
		socket.on('enable', message => {
			emit({type: 'enable', message})
		})
		socket.on('disable', message => {
			emit({type: 'disable', message})
		})
		socket.on('showBackground', message => {
			emit({type: 'showBackground', message})
		})
		socket.on('hideBackground', message => {
			emit({type: 'hideBackground', message})
		})
		socket.on('status', message => {
			emit({type: 'status', message})
		})
		socket.on('disconnect', () => {
			emit({type: 'disconnect'})
			emit(END)
		})
		return () => {}
	})
}

function *listenEnabledDisable(socket) {

	const channel = yield call(subscribe, socket)
	while (true) {
		const channel_message = yield take(channel)
		if (channel_message.type === 'enable') {
			yield put(whiteboardActions.enabled(channel_message.message))
		} else if (channel_message.type === 'disable') {
			yield put(whiteboardActions.disabled())
		}
	}
}

function *listenToSocketEvents(socket) {

	const channel = yield call(subscribe, socket)
	while (true) {
		const channel_message = yield take(channel)
		if (channel_message.type === 'actions') {
			yield put(whiteboardActions.actionsReceived(channel_message.message))
		} if (channel_message.type === 'showBackground') {
			yield put(whiteboardActions.setBackground(true))
		} else if (channel_message.type === 'hideBackground') {
			yield put(whiteboardActions.setBackground(false))
		}
	}
}

function *findOrStartDrawing() {
	const drawingData = yield select(drawingSelector)
	let currentDrawingId
	try {
		const currentDrawingData = yield callAny(findDrawingBySession, drawingData)
		currentDrawingId = currentDrawingData.id
	} catch (err) {
		if (err.error_code === errorCodes.NOT_FOUND) {
			currentDrawingId = yield callAny(startDrawing, drawingData)
		} else {
			currentDrawingId = ''
		}
		// TODO, handle only the non found error to create a new one
	}
	return currentDrawingId

}

function *trackActivity(action, mode) {
	const drawingData = yield select(drawingSelector)
	const properties = {
		sessionId: drawingData.phoenixSessionId,
	}
	let event
	if (action.type === actionTypes.WHITEBOARD_ENABLE) {

		if (mode === whiteboardModes.WHITEBOARD) {
			event = 'Whiteboard Started'
		} else {
			event = 'Annotate Started'
			properties.slideId = drawingData.slideId
		}
	} else {
		if (mode === whiteboardModes.WHITEBOARD) {
			event = 'Whiteboard Ended'
		} else {
			event = 'Annotate Ended'
		}
	}
	yield call(mixpanel.track, event, properties, false)
}

function *syncEnabledDisable(socket) {
	const eventActions = [
		actionTypes.WHITEBOARD_ENABLE,
		actionTypes.WHITEBOARD_DISABLE,
		actionTypes.WHITEBOARD_UPDATED,
	]
	const requestChannel = yield actionChannel(eventActions)
	const drawingData = yield select(drawingSelector)
	let currentMode = drawingData.mode
	while (true) {
		const action = yield take(requestChannel)
		let message, messageData
		if (action.type === actionTypes.WHITEBOARD_ENABLE) {
			const currentDrawingId = yield call(findOrStartDrawing)
			if (!currentDrawingId) {
				break
			}

			message = 'enable'
			messageData = {
				mode: action.mode,
				currentDrawingId,
			}
			currentMode = action.mode
		} else {
			message = 'disable'
			messageData = {}
		}
		yield spawn(socket.emit, message, messageData)
		yield spawn(trackActivity, action, currentMode)
	}
}


function *waitForSessionEnd() {
	yield take(actionTypes.WHITEBOARD_UNMOUNTED)
	return true
}

function listenToSocketDisconnection(socket) {

	return new Promise(resolve => {
		socket.on('disconnect', resolve)
	})
}

function *listenForSession(socket, action) {

	while (true) {
		try {
			const sessionData = yield select(slideSelector)
			if (sessionData.mode === whiteboardModes.NONE) {
				// wait for whiteboard session to be enabled
				yield take([
					actionTypes.WHITEBOARD_ENABLED,
				])
			}
			const drawingData = yield call(getDrawingData)

			const concurrentThreads = {
				events: call(listenToSocketEvents, socket, action),
				ended: take([
					actionTypes.WHITEBOARD_ENABLED,
					actionTypes.WHITEBOARD_DISABLED,
				]),
			}
			if (action.role === whiteboardRoles.TEACHER) {
				concurrentThreads.syncActions = call(syncActions, socket, drawingData)
			}
			yield race(concurrentThreads)
		} catch (err) {
			yield call(delay, 2000)
		}
	}
}

function getConcurrentThreads(socket, action) {
	const concurrentThreads = {
		socketDisconnect: call(listenToSocketDisconnection, socket),
		session: call(listenForSession, socket, action),
		sessionEnd: call(waitForSessionEnd, socket),
		listenEnabledDisable: call(listenEnabledDisable, socket),
	}
	if (action.role === whiteboardRoles.TEACHER) {
		concurrentThreads.syncEnabledDisable = call(syncEnabledDisable, socket)
	}
	return concurrentThreads
}

function *initializeSession(sessionId, action) {
	yield call(documentVisibility)
	const socket = yield call(startSocketConnection, sessionId, action.jwt)
	yield call(getSessionData, sessionId)
	const concurrentThreads = getConcurrentThreads(socket, action)
	const {sessionEnd} = yield race(concurrentThreads)
	if (socket) {
		yield call(socket.disconnect)
	}
	if (sessionEnd) {
		if (action.role === whiteboardRoles.TEACHER) {
			yield call(socket.emit, 'disable')
		}
		return socket
	} else {
		yield put(whiteboardActions.disconnected())
		throw new Error()
	}
}

function *handleWhiteboardMounted(action) {
	let socket
	try {
		const sessionId = yield call(getSessionId, action.sessionId, action.role)
		yield call(
			exponentialBackoff,
			initializeSession,
			[sessionId, action],
		)
	} catch (error) {
		const serverError = formatError(error)
		yield put(whiteboardActions.initializationFailed(serverError))
	} finally {
		if (yield cancelled()) {
			if (socket) {
				yield call(socket.disconnect)
			}
		}
	}
}

export default [
	takeLatest([
		actionTypes.WHITEBOARD_MOUNTED,
	], handleWhiteboardMounted),
]

export {
	handleWhiteboardMounted,
}
